import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "./firebase";
import { collection, getDocs, doc, setDoc } from "firebase/firestore";
import "./css/AdminPanel.css";

const AdminPanel = () => {
  const navigate = useNavigate();
  const [userCount, setUserCount] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check and update profile discovery
  const checkAndUpdateProfileDiscovery = async (usersCollection) => {
    try {
      const userSnapshot = await getDocs(usersCollection);
      const batch = [];
      
      for (const docSnapshot of userSnapshot.docs) {
        if (docSnapshot.id === 'RegisteredUsers') continue;
        
        const userData = docSnapshot.data();
        const profileImageUrls = userData.profileImageUrls || [];
        const profileDiscovery = userData.profileDiscovery;
        
        // Condition: profileImageUrls less than 3 AND profileDiscovery is true
        if (profileImageUrls.length < 3 && profileDiscovery === true) {
          batch.push(docSnapshot.id);
        }
      }
      
      // Update all users that need profileDiscovery set to false
      for (const userId of batch) {
        const userRef = doc(db, "users", userId);
        await setDoc(userRef, { profileDiscovery: false }, { merge: true });
        console.log(`Updated profileDiscovery to false for user: ${userId}`);
      }
      
      if (batch.length > 0) {
        console.log(`Updated ${batch.length} users with profileDiscovery = false`);
      }
    } catch (error) {
      console.error("Error checking and updating profileDiscovery:", error);
    }
  };

  // Check and update expired subscriptions
  const checkAndUpdateExpiredSubscriptions = async (usersCollection) => {
    try {
      const userSnapshot = await getDocs(usersCollection);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const batch = [];
      
      for (const docSnapshot of userSnapshot.docs) {
        if (docSnapshot.id === 'RegisteredUsers') continue;
        
        const userData = docSnapshot.data();
        const subscriptions = userData.subscriptions;
        
        if (subscriptions && subscriptions.validUntil) {
          let validUntilDate = subscriptions.validUntil;
          
          // Handle Firestore timestamp
          if (validUntilDate.seconds) {
            validUntilDate = new Date(validUntilDate.seconds * 1000);
          } else {
            validUntilDate = new Date(validUntilDate);
          }
          
          validUntilDate.setHours(0, 0, 0, 0);
          
          // Check if subscription is expired
          if (validUntilDate < today) {
            batch.push(docSnapshot.id);
          }
        }
      }
      
      // Update all users with expired subscriptions
      for (const userId of batch) {
        const userRef = doc(db, "users", userId);
        await setDoc(userRef, { 
          subscriptions: {
            availedBoost: 0,
            boostedUntil: null,
            planName: "Free",
            validUntil: ""
          }
        }, { merge: true });
        console.log(`Updated expired subscription to Free for user: ${userId}`);
      }
      
      if (batch.length > 0) {
        console.log(`Updated ${batch.length} users with expired subscriptions`);
      }
    } catch (error) {
      console.error("Error checking and updating expired subscriptions:", error);
    }
  };

  // Check and update expired isDisabled status
  const checkAndUpdateExpiredIsDisabled = async (usersCollection) => {
    try {
      const userSnapshot = await getDocs(usersCollection);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const batch = [];
      
      for (const docSnapshot of userSnapshot.docs) {
        if (docSnapshot.id === 'RegisteredUsers') continue;
        
        const userData = docSnapshot.data();
        const expiresAt = userData.expiresAt;
        
        // Check if expiresAt exists and is expired
        if (expiresAt) {
          let expiresAtDate;
          
          // Handle Firestore timestamp
          if (expiresAt.seconds) {
            expiresAtDate = new Date(expiresAt.seconds * 1000);
          } else {
            expiresAtDate = new Date(expiresAt);
          }
          
          expiresAtDate.setHours(0, 0, 0, 0);
          
          // Check if expiresAt is before today (expired)
          if (expiresAtDate < today) {
            batch.push(docSnapshot.id);
          }
        }
      }
      
      // Update all users with expired isDisabled status
      for (const userId of batch) {
        const userRef = doc(db, "users", userId);
        await setDoc(userRef, { 
          isDisabled: false,
          expiresAt: ""
        }, { merge: true });
        console.log(`Updated isDisabled to false and cleared expiresAt for user: ${userId}`);
      }
      
      if (batch.length > 0) {
        console.log(`Updated ${batch.length} users with expired isDisabled status`);
      }
    } catch (error) {
      console.error("Error checking and updating expired isDisabled:", error);
    }
  };

  // Fetch user count and apply conditions when component mounts
  useEffect(() => {
    const initializeAdminPanel = async () => {
      try {
        setLoading(true);
        const usersCollection = collection(db, "users");
        
        // Run all three update conditions in parallel
        await Promise.all([
          checkAndUpdateProfileDiscovery(usersCollection),
          checkAndUpdateExpiredSubscriptions(usersCollection),
          checkAndUpdateExpiredIsDisabled(usersCollection)
        ]);
        
        // Fetch updated user count after conditions are applied
        const userSnapshot = await getDocs(usersCollection);
        const userList = userSnapshot.docs
          .filter((doc) => doc.id !== 'RegisteredUsers')
          .map((doc) => doc.data());
  
        setUserCount(userList.length);
        setLoading(false);
      } catch (error) {
        console.error("Error initializing admin panel: ", error);
        setLoading(false);
      }
    };
    
    initializeAdminPanel();
  }, []);

  // Handle Logout
  const handleLogout = async () => {
    await auth.signOut();
    navigate("/login");
  };

  // Navigate to Manage Users page
  const handleManageUsersClick = () => {
    navigate("/manage-users");
  };

  return (
    <div className="admin-panel-container">
      <div className="dashboard">
        <div className="cards-container">
          <div className="card" onClick={handleManageUsersClick}>
            <i className="fas fa-users" style={{ fontSize: '55px', marginBottom: '10px', textAlign: 'center' }}></i>
            <h3>Manage Users</h3>
            
            {loading ? (
              <div className="loader">
                <div className="spinner"></div>
              </div>
            ) : (
              <h3>Total Users: {userCount}</h3>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;